import React, { useCallback, useEffect, useState } from 'react';
import { useInterval } from 'react-use';
import { generate as shortUuid } from 'short-uuid';

import { useLocation } from 'wouter';
import { Clickable, ClickableVariant } from '../../components/Clickable';
import { EventCard } from '../../components/EventCard';
import { Footer } from '../../components/Footer';
import { InfiniteScroller } from '../../components/InfiniteScroller';
import { Status } from '../../components/Status';
import { Toolbar, BulkEdit } from '../../components/Toolbar';
// import { TicketsIndicator } from '../../components/TicketsIndicator';
import { MINUTE } from '../../config';
import { c, getEventServerStatus, isPending, isRejected } from '../../utils';
import {
  fetchUpcomingEvents,
  setOverlay,
  OverlayType,
  setBulkEdit,
} from '../../actions';
import { useAppDispatch, useAppSelector } from '../../hooks';
import {
  allEvents,
  getStatusOfGetUpcomingEvents,
  isBulkEditing,
  lastEventLoaded,
  shouldLoadMoreEvents,
} from '../../selectors';
import { GatherlyEventServerStatus } from '../../enums';
import classes from './EventsPage.module.scss';
import { IGatherlyEvent } from '@gatherly/types';

const selectedEventIdSet: Set<string> = new Set();

export function EventsPage() {
  const dispatch = useAppDispatch();
  const [, setLocation] = useLocation();

  const events = useAppSelector(allEvents);
  const status = useAppSelector(getStatusOfGetUpcomingEvents);
  const isMakingRequest = isPending(status);
  const hasError = isRejected(status);
  const hasMore = useAppSelector(shouldLoadMoreEvents);
  const lastEvent = useAppSelector(lastEventLoaded);
  const hasEvents = !!events?.length;
  const [selectedEventIds, setSelectedEventIds] = useState<string[] | []>([]);
  const isEditing: boolean = useAppSelector(isBulkEditing);

  useEffect(() => {
    fetchUpcomingEvents(dispatch);
    return () => selectedEventIdSet.clear();
  }, []);

  useEffect(() => {
    if (!isEditing) {
      selectedEventIdSet.clear();
      setSelectedEventIds([]);
    }
  }, [isEditing]);

  const onClickNewEvent = useCallback(() => {
    setOverlay(dispatch, OverlayType.NEW_EVENT, {
      allowDismiss: false,
    });
  }, []);

  const handleLoadMore = useCallback(() => {
    fetchUpcomingEvents(dispatch, lastEvent);
  }, [lastEvent]);

  // Force event cards to recalculate status pill every minute
  const [refreshKey, setRefreshKey] = useState(shortUuid());
  useInterval(() => {
    setRefreshKey(shortUuid());
  }, MINUTE);

  const handleCancelBulkEdit = useCallback(() => {
    setBulkEdit(dispatch, false);
    selectedEventIdSet.clear();
    setSelectedEventIds([]);
  }, []);

  const handleBulkDelete = useCallback(() => {
    setOverlay(dispatch, OverlayType.DELETE_EVENT, {
      eventIds: selectedEventIds,
    });
  }, [selectedEventIds, dispatch]);

  const toggleSelectedEventCard = useCallback(
    (eventId: string) => {
      const event = events.find(
        (event: IGatherlyEvent) => event.eventId === eventId,
      );
      const serverStatus = getEventServerStatus(
        event?.startTime,
        event?.stopTime,
      );

      if (serverStatus !== GatherlyEventServerStatus.PRELAUNCH) {
        return;
      }
      if (selectedEventIdSet.has(eventId)) {
        selectedEventIdSet.delete(eventId);
      } else {
        selectedEventIdSet.add(eventId);
      }
      setSelectedEventIds([...selectedEventIdSet]);
    },
    [events],
  );

  const handleClickEventCard = useCallback(
    (eventId: string) => {
      if (isEditing) {
        toggleSelectedEventCard(eventId);
      } else {
        setLocation(`/events/${eventId}`);
      }
    },
    [isEditing],
  );

  return (
    <div
      className={c(
        classes.EventsPage,
        'page-inner overflow-hidden bg-color-gradient-faded',
      )}
    >
      <Toolbar title="Events" classNames="bg-frosted" />
      <div
        className={c(
          classes.list,
          'flex flex-1 flex-col gap-1 justify-between page-inner__content',
        )}
      >
        {/* [2026-07-03] Promote v2 to account.gatherly.io users */}
        {
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              background: '#fff',
              border: '1px solid #cfe0dd',
              borderRadius: 12,
              padding: '15px 16px',
              marginBottom: 24,
            }}
          >
            <span
              style={{
                width: 42,
                height: 42,
                borderRadius: 11,
                background: '#EDE7F7',
                color: '#5b3a8a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="21"
                height="21"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                <path d="M20 3v4" />
                <path d="M22 5h-4" />
                <path d="M4 17v2" />
                <path d="M5 18H3" />
              </svg>
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  background: '#E1F5EE',
                  color: '#0F6E56',
                  fontSize: 11,
                  fontWeight: 500,
                  padding: '3px 9px',
                  borderRadius: 99,
                  marginBottom: 6,
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
                No new account needed
              </span>
              <div style={{ fontSize: 15, fontWeight: 500, color: '#2c2c2a' }}>
                The new Event Manager is here
              </div>
              <div style={{ fontSize: 12, color: '#888780', marginTop: 2 }}>
                Faster, cleaner, and easier to use — sign in with your existing
                credentials.
              </div>
            </div>
            <button
              onClick={() => {
                window.location.href = 'https://v2.event-manager.gatherly.io';
              }}
              style={{
                background: '#5b3a8a',
                border: 'none',
                color: '#fff',
                fontSize: 13,
                fontWeight: 500,
                padding: '9px 16px',
                borderRadius: 8,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              Try it now
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 12h14" />
                <path d="m12 5 7 7-7 7" />
              </svg>
            </button>
          </div>
        }
        <div className="flex flex-col md:flex-row-reverse gap-1">
          {/* <div className="flex flex-1 gap-1 justify-end md:pl2 md:flex-col md:justify-start md:items-end"> */}
          <div className="flex flex-1 gap-1">
            <Clickable
              data-cy="create-event"
              onClick={onClickNewEvent}
              variant={ClickableVariant.BUTTON_TERTIARY}
            >
              Create event
            </Clickable>
            <BulkEdit
              editLabel="Delete events"
              enableEditing={() => setBulkEdit(dispatch, true)}
              isEditing={isEditing}
              onCancel={handleCancelBulkEdit}
              onDelete={handleBulkDelete}
              selectedCount={selectedEventIds.length}
            />
          </div>
          {/* <TicketsIndicator /> */}
        </div>

        <div
          className={c(
            'flex flex-col gap-1',
            !hasEvents && 'flex-1 mb8 items-center justify-center',
          )}
        >
          <InfiniteScroller
            className="flex flex-col gap-1"
            hasMore={hasMore}
            onLoad={handleLoadMore}
          >
            {events.map(event => (
              <EventCard
                event={event}
                onClick={() => handleClickEventCard(event.eventId)}
                isSelected={selectedEventIdSet.has(event.eventId)}
                isEditing={isEditing}
                key={`${refreshKey}-${event.eventId}`}
              />
            ))}
          </InfiniteScroller>
          {(isMakingRequest || !hasEvents) && (
            <Status
              className="color-shade-80"
              withLoader={isMakingRequest}
              message={
                isMakingRequest ? (
                  `Loading${hasEvents ? ' more' : ''} events...`
                ) : hasError ? (
                  'Error retrieving events!'
                ) : (
                  <span>
                    No events yet.{' '}
                    <Clickable
                      data-cy="create-event"
                      onClick={onClickNewEvent}
                      variant={ClickableVariant.BUTTON_TERTIARY}
                    >
                      Create my first event!
                    </Clickable>
                  </span>
                )
              }
            />
          )}
          {!hasMore && !isMakingRequest && hasEvents && (
            <div className="flex flex-col items-center text-center gap-1 color-shade-80 my4">
              <p className="subheading">That's it!</p>
              <p className="body">
                You have created {events.length} Gatherly event
                {events.length > 1 ? 's' : ''} so far.
                <br />
                Make a new one?
              </p>
              <Clickable
                onClick={onClickNewEvent}
                variant={ClickableVariant.BUTTON_TERTIARY}
              >
                New Event
              </Clickable>
            </div>
          )}
        </div>
        <Footer />
      </div>
    </div>
  );
}
